import { Add } from "@mui/icons-material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { Box, Button, Stack, Typography } from "@mui/material";
import {
    getApplicationTypes,
    getApplicationTypeSeasons,
    getSeasons,
    postApplicationTypes,
    postApplicationTypeSeasons,
    postSeasons,
} from "apis/controllers/settings";
import { useSnackbar } from "notistack";
import CustomSimpleAccordion from "qubCommon/Accordions/CustomSimpleAccordion";
import { showApiMessage } from "qubCommon/GlobalSnackbar";
import { useEffect, useState } from "react";
import ApplicationTypeDialog from "./ApplicationTypeDialog";
import ApplicationTypeSeasonDialog from "./ApplicationTypeSeasonDialog";
import ApplicationTypeSeasonsTable from "./ApplicationTypeSeasonTable";
import ApplicationTypesTable from "./ApplicationTypesTable";
import ProgramPeriodDialog from "./ProgramPeriodDialog";
import ProgramPeriodsTable from "./ProgramPeriodsTable";

const ProgramSettings = () => {
    // --- Local state (replace with API data) ---

    const [periods, setPeriods] = useState([]);
    const [appTypes, setAppTypes] = useState([]);
    const [appTypeSeasons, setAppTypeSeasons] = useState([]);
    const [loading, setLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [periodsResponse, typesResponse, appTypeSeasonsResponse] =
                await Promise.all([
                    getSeasons(),
                    getApplicationTypes(),
                    getApplicationTypeSeasons(),
                ]);

            setPeriods(periodsResponse);
            setAppTypes(typesResponse);
            setAppTypeSeasons(appTypeSeasonsResponse);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    // --- Dialog states ---
    const [openPeriodDialog, setOpenPeriodDialog] = useState(false);
    const [openAppTypeDialog, setOpenAppTypeDialog] = useState(false);

    const [editingPeriod, setEditingPeriod] = useState(null);
    const [editingAppType, setEditingAppType] = useState(null);

    const [openTypeSeasonDialog, setOpenTypeSeasonDialog] = useState(false);
    const [editingTypeSeason, setEditingTypeSeason] = useState(null);

    // --- Period logic ---
    const handleAddOrEditPeriod = async (data) => {
        setLoading(true);
        try {
            let updatedPeriods = [...periods];

            if (editingPeriod) {
                updatedPeriods = periods.map((p) =>
                    p.id === editingPeriod.id ? { ...p, ...data } : p
                );
            } else {
                updatedPeriods = [...periods, { id: null, ...data }];
            }

            const res = await postSeasons(updatedPeriods);
            await fetchData(); // Refresh data after save
        } catch (error) {
            console.error("Error saving periods:", error);
        } finally {
            setLoading(true);
        }
        setOpenPeriodDialog(false);
        setEditingPeriod(null);
    };

    const handleDeletePeriod = async (row) => {
        if (
            window.confirm(`Θέλετε σίγουρα να διαγράψετε την περίοδο ${row?.abbr};`)
        ) {
            try {
                let updatedPeriods = [...periods];

                if (row) {
                    updatedPeriods = periods.map((p) =>
                        p.id === row.id ? { ...p, isDeleted: true } : p
                    );

                    const res = await postSeasons(updatedPeriods);
                    const ok = showApiMessage(res);

                    if (!ok) return;
                    await fetchData(); // Refresh data after save
                }
            } catch (error) {
                console.error("Error saving periods:", error);
            }
        }
    };

    // --- Application type logic ---
    // --- Application type logic ---
    const handleAddOrEditAppType = async (data) => {
        if (loading) return;
        setLoading(true);

        try {
            let updatedAppTypes = [...appTypes];

            if (editingAppType) {
                updatedAppTypes = appTypes.map((a) =>
                    a.id === editingAppType.id ? { ...a, ...data } : a
                );

                const res = await postApplicationTypes(updatedAppTypes);

                if (res?.status === "success") {
                    enqueueSnackbar(
                        res?.message || "Το είδος αίτησης ενημερώθηκε με επιτυχία",
                        {
                            variant: "success",
                        }
                    );
                } else {
                    enqueueSnackbar(
                        res?.message || "Πρόβλημα κατά την ενημέρωση του είδους αίτησης",
                        { variant: "error" }
                    );
                    return;
                }
            } else {
                updatedAppTypes = [...appTypes, data];

                const res = await postApplicationTypes(updatedAppTypes);

                if (res?.status === "success") {
                    enqueueSnackbar(
                        res?.message || "Το είδος αίτησης προστέθηκε με επιτυχία",
                        {
                            variant: "success",
                        }
                    );
                } else {
                    enqueueSnackbar(
                        res?.message || "Πρόβλημα κατά την προσθήκη είδους αίτησης!",
                        { variant: "error" }
                    );
                    return;
                }
            }

            await fetchData();
            setOpenAppTypeDialog(false);
            setEditingAppType(null);
        } catch (error) {
            console.error("Error saving application types:", error);
            enqueueSnackbar("Σφάλμα επικοινωνίας με τον server", {
                variant: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAppType = async (row) => {
        if (
            window.confirm(
                `Θέλετε σίγουρα να διαγράψετε το είδος αίτησης ${row?.abbr};`
            )
        ) {
            try {
                let updatedAppTypes = [...appTypes];

                if (row) {
                    updatedAppTypes = appTypes.map((p) =>
                        p.id === row.id ? { ...p, isDeleted: true } : p
                    );

                    const res = await postApplicationTypes(updatedAppTypes);
                    const ok = showApiMessage(res);

                    if (!ok) return;
                    await fetchData(); // Refresh data after save
                }
            } catch (error) {
                console.error("Error saving periods:", error);
            }
        }
    };

    // --- Open dialogs ---
    const handleNewPeriod = () => {
        setEditingPeriod(null);
        setOpenPeriodDialog(true);
    };

    const handleNewAppType = () => {
        setEditingAppType(null);
        setOpenAppTypeDialog(true);
    };

    const handleNewTypeSeason = () => {
        setEditingTypeSeason(null);
        setOpenTypeSeasonDialog(true);
    };

    const handleEditTypeSeason = (row) => {
        setEditingTypeSeason(row);
        setOpenTypeSeasonDialog(true);
    };

    const handleAddOrEditTypeSeason = async (data) => {
        console.log("Saved Application Type Season:", data);
        // TODO: call API, refresh list, etc.
        setOpenTypeSeasonDialog(false);
        setEditingTypeSeason(null);
        setLoading(true);
        try {
            let updatedAppTypeSeasons = [...appTypeSeasons];

            if (editingTypeSeason) {
                updatedAppTypeSeasons = appTypeSeasons.map((p) =>
                    p.id === editingTypeSeason.id ? { ...p, ...data } : p
                );
            } else {
                updatedAppTypeSeasons = [...appTypeSeasons, { id: null, ...data }];
            }

            const res = await postApplicationTypeSeasons(updatedAppTypeSeasons);
            const ok = showApiMessage(res);

            if (!ok) return;
            await fetchData(); // Refresh data after save
        } catch (error) {
            console.error("Error saving periods:", error);
        } finally {
            setLoading(true);
        }
        setOpenPeriodDialog(false);
        setEditingPeriod(null);
    };

    const handleStartPauseTypeSeason = async (row) => {
        if (window.confirm(`Θέλετε σίγουρα να  θέσετε ${row.paused ? 'ξανα σε λειτουργία' : 'σε παύση'} το πρόγραμμα;`)) {
            try {
                let updatedAppTypeSeasons = [...appTypeSeasons];

                if (row) {
                    updatedAppTypeSeasons = appTypeSeasons.map((p) =>
                        p.id === row.id ? { ...p, paused: !row.paused } : p
                    );

                    const res = await postApplicationTypeSeasons(updatedAppTypeSeasons);
                    const ok = showApiMessage(res);

                    if (!ok) return;
                    await fetchData(); // Refresh data after save
                }
            } catch (error) {
                console.error("Error saving periods:", error);
            }
        }
    };

    const handleDeleteTypeSeason = async (row) => {
        if (window.confirm(`Θέλετε σίγουρα να διαγράψετε το πρόγραμμα;`)) {
            try {
                let updatedAppTypeSeasons = [...appTypeSeasons];

                if (row) {
                    updatedAppTypeSeasons = appTypeSeasons.map((p) =>
                        p.id === row.id ? { ...p, isDeleted: true } : p
                    );

                    const res = await postApplicationTypeSeasons(updatedAppTypeSeasons);
                    const ok = showApiMessage(res);

                    if (!ok) return;
                    await fetchData(); // Refresh data after save
                }
            } catch (error) {
                console.error("Error saving periods:", error);
            }
        }
    };

    // --- Edit callbacks (triggered from tables) ---
    const handleEditPeriod = (row) => {
        setEditingPeriod(row);
        setOpenPeriodDialog(true);
    };

    const handleEditAppType = (row) => {
        setEditingAppType(row);
        setOpenAppTypeDialog(true);
    };

    return (
        <Box sx={{ p: 3, minHeight: "70vh", mx: "auto", maxWidth: "1400px" }}>
            <Stack spacing={2}>
                {/* --- Periods Accordion --- */}
                <CustomSimpleAccordion
                    title={
                        <Stack direction="row" gap={1} alignItems="center">
                            <CalendarTodayIcon color="primary" fontSize="small" />
                            <Typography color="primary" sx={{ fontWeight: 700 }}>
                                Περίοδοι Προγράμματος
                            </Typography>
                        </Stack>
                    }
                    content={
                        <>
                            <Button
                                startIcon={<Add />}
                                variant="contained"
                                color="primary"
                                sx={{ mt: 1 }}
                                onClick={handleNewPeriod}
                            >
                                Νέα Περίοδος
                            </Button>
                            <ProgramPeriodsTable
                                rows={periods}
                                onEdit={handleEditPeriod}
                                onDelete={handleDeletePeriod}
                            />
                        </>
                    }
                />

                {/* --- Application Types Accordion --- */}
                <CustomSimpleAccordion
                    title={
                        <Stack direction="row" gap={1} alignItems="center">
                            <ListAltIcon color="primary" fontSize="small" />
                            <Typography color="primary" sx={{ fontWeight: 700 }}>
                                Είδη Αιτήσεων
                            </Typography>
                        </Stack>
                    }
                    content={
                        <>
                            <Button
                                startIcon={<Add />}
                                variant="contained"
                                color="primary"
                                sx={{ mt: 1 }}
                                onClick={handleNewAppType}
                            >
                                Νέος Τύπος Αίτησης
                            </Button>
                            <ApplicationTypesTable
                                rows={appTypes}
                                onEdit={handleEditAppType}
                                onDelete={handleDeleteAppType}
                            />
                        </>
                    }
                />
                {periods.map((season) => {
                    const seasonRows = appTypeSeasons
                        .filter((a) => a.seasonId === season.id)
                        .map((a) => ({
                            ...a,
                            applicationType:
                                appTypes.find((t) => t.id === a.typeId)?.description || "-",
                            season:
                                season.description || season.year || `Περίοδος ${season.id}`,
                        }));

                    if (!seasonRows.length) return null;

                    return (
                        <CustomSimpleAccordion
                            key={season.id}
                            title={
                                <Stack direction="row" gap={1} alignItems="center">
                                    <ListAltIcon color="primary" fontSize="small" />
                                    <Typography color="primary" sx={{ fontWeight: 700 }}>
                                        Περίοδοι ανά Είδος Αίτησης –{" "}
                                        {season.description || season.year || season.id}
                                    </Typography>
                                </Stack>
                            }
                            content={
                                <ApplicationTypeSeasonsTable
                                    rows={seasonRows}
                                    onEdit={handleEditTypeSeason}
                                    onStartPause={handleStartPauseTypeSeason}
                                    onDelete={handleDeleteTypeSeason}
                                />
                            }
                        />
                    );
                })}
                <Button
                    startIcon={<Add />}
                    variant="contained"
                    color="primary"
                    sx={{ mt: 1, width: 360, textTransform: "none" }}
                    onClick={handleNewTypeSeason}
                >
                    Νέο είδος αίτησης για επιλεγμένη περίοδο
                </Button>
            </Stack>

            {/* --- Dialogs --- */}
            <ProgramPeriodDialog
                open={openPeriodDialog}
                onClose={() => setOpenPeriodDialog(false)}
                onSubmit={handleAddOrEditPeriod}
                defaultValues={
                    editingPeriod || { year: "", start: "", end: "", isActive: false }
                }
            />

            <ApplicationTypeDialog
                open={openAppTypeDialog}
                onClose={() => setOpenAppTypeDialog(false)}
                onSubmit={handleAddOrEditAppType}
                defaultValues={
                    editingAppType || { code: "", description: "", isActive: false }
                }
            />
            <ApplicationTypeSeasonDialog
                open={openTypeSeasonDialog}
                onClose={() => setOpenTypeSeasonDialog(false)}
                onSubmit={handleAddOrEditTypeSeason}
                defaultValues={editingTypeSeason || {}}
                seasons={periods.map((p) => ({ Value: p.id, Label: p.description }))}
                applicationTypes={appTypes.map((at) => ({
                    Value: at.id,
                    Label: at.description,
                }))}
            />
        </Box>
    );
};

export default ProgramSettings;
